import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, Clock, Calendar, AlertTriangle, CheckCircle, TrendingUp,
  Briefcase, Building, Award, Target, Activity, Timer, LogIn, LogOut, Zap, X,
  CalendarX, Palmtree, FileText
} from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from 'recharts';
import { dashboardService, employeeService, EmployeeDetailStats } from '../services';

const EmployeeStats = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const employeeId = searchParams.get('id');

  const [stats, setStats] = useState<EmployeeDetailStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'weekly' | 'monthly'>('weekly');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [showPhotoZoom, setShowPhotoZoom] = useState(false);

  useEffect(() => {
    if (employeeId) {
      const id = parseInt(employeeId);
      loadStats(id);
      loadPhoto(id);
    } else {
      setError(t('employeeStats.noEmployeeId'));
      setLoading(false);
    }
  }, [employeeId]);

  const loadPhoto = async (id: number) => {
    try {
      const res = await employeeService.getPhoto(id);
      if (res.success && res.data?.photoUrl) {
        setPhotoUrl(res.data.photoUrl);
      }
    } catch { /* ignore */ }
  };

  const loadStats = async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await dashboardService.getEmployeeDetailStats(id);
      if (response.success && response.data) {
        setStats(response.data);
      } else {
        setError(response.message || t('employeeStats.loadError'));
      }
    } catch (err: any) {
      setError(err.message || t('employeeStats.genericError'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDayName = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      weekday: 'short'
    });
  };

  const formatMinutes = (minutes: number) => {
    if (minutes < 60) return `${minutes} ${t('employeeStats.minuteShort')}`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours} ${t('employeeStats.hourShort')} ${mins} ${t('employeeStats.minuteShort')}` : `${hours} ${t('employeeStats.hourShort')}`;
  };

  const formatHours = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-slate-500">
        <AlertTriangle size={48} className="mb-4 text-red-400" />
        <p className="text-lg font-medium">{error || t('common.noData')}</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium flex items-center gap-2"
        >
          <ArrowLeft size={16} /> {t('common.goBack')}
        </button>
      </div>
    );
  }

  // Hesaplamalar - is gunlerine gore dogru oran
  const weeklyWorkDays = stats.weeklyWorkDays || 5;
  const monthlyWorkDays = stats.monthlyWorkDays || 22;
  const weeklyAttendanceRate = Math.min(100, Math.round((stats.weeklyPresentCount / weeklyWorkDays) * 100));
  const monthlyAttendanceRate = Math.min(100, Math.round((stats.monthlyPresentCount / monthlyWorkDays) * 100));
  const weeklyPunctualityRate = stats.weeklyPresentCount > 0
    ? Math.round(((stats.weeklyPresentCount - stats.weeklyLateCount) / stats.weeklyPresentCount) * 100)
    : 0;
  const monthlyPunctualityRate = stats.monthlyPresentCount > 0
    ? Math.round(((stats.monthlyPresentCount - stats.monthlyLateCount) / stats.monthlyPresentCount) * 100)
    : 0;

  // Aktif tab'a gore filtrelenmis girisler
  const filteredEntries = (() => {
    const now = new Date();
    const cutoff = activeTab === 'weekly'
      ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7)
      : new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
    return stats.recentEntries.filter(e => new Date(e.date) >= cutoff);
  })();

  // Ortalama calisma suresi (dakika)
  const totalWorkedMinutes = filteredEntries.reduce((sum, e) => sum + e.workedMinutes, 0);
  const avgWorkedMinutes = filteredEntries.length > 0
    ? Math.round(totalWorkedMinutes / filteredEntries.filter(e => e.workedMinutes > 0).length) || 0
    : 0;

  // Performans skoru (0-100)
  const attendanceScore = activeTab === 'weekly' ? weeklyAttendanceRate : monthlyAttendanceRate;
  const punctualityScore = activeTab === 'weekly' ? weeklyPunctualityRate : monthlyPunctualityRate;
  const overallScore = Math.round((attendanceScore + punctualityScore) / 2);

  // Grafik verileri - filtrelenmis girislerden
  const chartData = filteredEntries.slice().reverse().map(entry => ({
    day: formatDayName(entry.date),
    date: formatDate(entry.date),
    calisma: Math.round(entry.workedMinutes / 60 * 10) / 10,
    gecikme: entry.lateMinutes
  }));

  // Aktif tab'a gore filtrelenmis devamsizlik
  const filteredAbsentDates = (() => {
    if (!stats.absentDates) return [];
    const now = new Date();
    const cutoff = activeTab === 'weekly'
      ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7)
      : new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
    return stats.absentDates.filter(a => new Date(a.date) >= cutoff);
  })();

  const absentOnly = filteredAbsentDates.filter(a => a.status === 'Absent').length;
  const onLeaveOnly = filteredAbsentDates.filter(a => a.status === 'OnLeave').length;
  const holidayOnly = filteredAbsentDates.filter(a => a.status === 'Holiday').length;

  // Pie chart verileri
  const presentCount = activeTab === 'weekly' ? stats.weeklyPresentCount : stats.monthlyPresentCount;
  const pieData = [
    { name: t('employeeStats.present'), value: presentCount, color: '#10b981' },
    ...(absentOnly > 0 ? [{ name: t('employeeStats.absent'), value: absentOnly, color: '#ef4444' }] : []),
    ...(onLeaveOnly > 0 ? [{ name: t('employeeStats.onLeave'), value: onLeaveOnly, color: '#3b82f6' }] : []),
    ...(holidayOnly > 0 ? [{ name: t('employeeStats.holiday'), value: holidayOnly, color: '#f59e0b' }] : []),
  ];

  // Performans rengi
  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-blue-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 70) return 'bg-blue-500';
    if (score >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 90) return t('employeeStats.excellent');
    if (score >= 70) return t('employeeStats.good');
    if (score >= 50) return t('employeeStats.average');
    return t('employeeStats.low');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2.5 hover:bg-slate-100 rounded-xl transition-colors border border-slate-200"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{t('employeeStats.title')}</h1>
            <p className="text-slate-500 text-sm">{t('employeeStats.subtitle')}</p>
          </div>
        </div>

        {/* Tab Switch */}
        <div className="flex bg-slate-100 rounded-xl p-1">
          <button
            onClick={() => setActiveTab('weekly')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'weekly'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t('employeeStats.weekly')}
          </button>
          <button
            onClick={() => setActiveTab('monthly')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'monthly'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t('employeeStats.monthly')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sol Kolon - Profil ve Performans */}
        <div className="space-y-6">
          {/* Profil Karti */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>

            <div className="relative">
              <div className="flex items-start gap-4 mb-6">
                {photoUrl ? (
                  <img src={photoUrl} alt={stats.fullName} className="w-16 h-16 rounded-xl border-2 border-white/20 object-cover shadow-lg cursor-pointer hover:ring-2 hover:ring-white/40 transition-all" onClick={() => setShowPhotoZoom(true)} />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center text-white font-bold text-xl border border-white/20">
                    {stats.fullName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="flex-1">
                  <h2 className="text-lg font-bold">{stats.fullName}</h2>
                  {stats.employeeCode && (
                    <p className="text-white/60 text-sm flex items-center gap-1.5 mt-0.5">
                      <User size={12} /> {stats.employeeCode}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                {stats.departmentName && (
                  <div className="flex items-center gap-2 text-white/70 text-sm">
                    <Building size={14} />
                    <span>{stats.departmentName}</span>
                  </div>
                )}
                {stats.positionName && (
                  <div className="flex items-center gap-2 text-white/70 text-sm">
                    <Briefcase size={14} />
                    <span>{stats.positionName}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Performans Skoru */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <Award size={18} className="text-amber-500" />
                {t('employeeStats.performanceScore')}
              </h3>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                overallScore >= 70 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
              }`}>
                {getScoreLabel(overallScore)}
              </span>
            </div>

            <div className="flex items-center justify-center mb-6">
              <div className="relative w-32 h-32">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="#e2e8f0"
                    strokeWidth="12"
                    fill="none"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke={overallScore >= 70 ? '#10b981' : overallScore >= 50 ? '#f59e0b' : '#ef4444'}
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray={`${overallScore * 3.52} 352`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`text-3xl font-bold ${getScoreColor(overallScore)}`}>{overallScore}</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-600">{t('employeeStats.attendanceRate')}</span>
                  <span className="font-semibold text-slate-800">%{attendanceScore}</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${getScoreBgColor(attendanceScore)}`}
                    style={{ width: `${attendanceScore}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-600">{t('employeeStats.punctualityRate')}</span>
                  <span className="font-semibold text-slate-800">%{punctualityScore}</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${getScoreBgColor(punctualityScore)}`}
                    style={{ width: `${punctualityScore}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Devam Dagilimi - Pie Chart */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Target size={18} className="text-blue-500" />
              {t('employeeStats.attendanceDistribution')}
            </h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={70}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-2">
              {pieData.map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <span className="text-sm text-slate-600">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Orta ve Sag Kolon */}
        <div className="lg:col-span-2 space-y-6">
          {/* Ozet Metrikler */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-green-100 rounded-lg">
                  <CheckCircle size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">
                    {activeTab === 'weekly' ? stats.weeklyPresentCount : stats.monthlyPresentCount}
                    <span className="text-sm font-normal text-slate-400">/{activeTab === 'weekly' ? weeklyWorkDays : monthlyWorkDays}</span>
                  </p>
                  <p className="text-xs text-slate-500">{t('employeeStats.presentDays')}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-red-100 rounded-lg">
                  <AlertTriangle size={20} className="text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">
                    {activeTab === 'weekly' ? stats.weeklyAbsentCount : stats.monthlyAbsentCount}
                  </p>
                  <p className="text-xs text-slate-500">{t('employeeStats.absentDays')}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-amber-100 rounded-lg">
                  <Clock size={20} className="text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">
                    {activeTab === 'weekly' ? stats.weeklyLateCount : stats.monthlyLateCount}
                  </p>
                  <p className="text-xs text-slate-500">{t('employeeStats.lateDays')}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-blue-100 rounded-lg">
                  <Timer size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">
                    {formatMinutes(activeTab === 'weekly' ? stats.weeklyLateMinutes : stats.monthlyLateMinutes)}
                  </p>
                  <p className="text-xs text-slate-500">{t('employeeStats.totalLateness')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Calisma Suresi Grafigi */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <Activity size={18} className="text-indigo-500" />
                {t('employeeStats.dailyWorkDuration')}
              </h3>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500">{t('employeeStats.average')}:</span>
                <span className="font-bold text-slate-800">{formatHours(avgWorkedMinutes)}</span>
              </div>
            </div>
            <div className="h-64">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorWork" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                      formatter={(value: number) => [`${value} ${t('employeeStats.hours')}`, t('employeeStats.work')]}
                    />
                    <Area
                      type="monotone"
                      dataKey="calisma"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorWork)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  {t('employeeStats.insufficientData')}
                </div>
              )}
            </div>
          </div>

          {/* Gecikme Grafigi */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <Zap size={18} className="text-amber-500" />
                {t('employeeStats.latenessAnalysis')}
              </h3>
            </div>
            <div className="h-48">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                      formatter={(value: number) => [`${value} ${t('employeeStats.minuteShort')}`, t('employeeStats.lateness')]}
                    />
                    <Bar dataKey="gecikme" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  {t('employeeStats.insufficientData')}
                </div>
              )}
            </div>
          </div>

          {/* {t('employeeStats.absent')}lik Detaylari */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <CalendarX size={18} className="text-red-500" />
                  {t('employeeStats.absenceDetails')}
                </h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  {activeTab === 'weekly' ? t('employeeStats.last7Days') : t('employeeStats.last30Days')} — {activeTab === 'weekly' ? weeklyWorkDays : monthlyWorkDays} {t('employeeStats.workDays')}
                </p>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-red-50 text-red-700 rounded-full font-medium">
                  <CalendarX size={12} /> {absentOnly} {t('employeeStats.absent')}
                </span>
                {onLeaveOnly > 0 && (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full font-medium">
                    <FileText size={12} /> {onLeaveOnly} {t('employeeStats.onLeave')}
                  </span>
                )}
                {holidayOnly > 0 && (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full font-medium">
                    <Palmtree size={12} /> {holidayOnly} {t('employeeStats.holiday')}
                  </span>
                )}
              </div>
            </div>
            {filteredAbsentDates.length === 0 ? (
              <div className="px-6 py-12 text-center text-slate-400">
                <CheckCircle size={32} className="mx-auto mb-2 opacity-40" />
                <p className="font-medium">{t('employeeStats.noAbsence')}</p>
                <p className="text-xs mt-1">{t('employeeStats.presentAllDays')}</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredAbsentDates.map((item, index) => (
                  <div key={index} className="px-6 py-3 flex items-center justify-between hover:bg-slate-50/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                        item.status === 'Absent' ? 'bg-red-100' :
                        item.status === 'OnLeave' ? 'bg-blue-100' : 'bg-amber-100'
                      }`}>
                        {item.status === 'Absent' && <CalendarX size={16} className="text-red-600" />}
                        {item.status === 'OnLeave' && <FileText size={16} className="text-blue-600" />}
                        {item.status === 'Holiday' && <Palmtree size={16} className="text-amber-600" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700">{formatDate(item.date)}</p>
                        <p className="text-xs text-slate-400 capitalize">{item.dayName}</p>
                      </div>
                    </div>
                    <div>
                      {item.status === 'Absent' && (
                        <span className="px-2.5 py-1 bg-red-50 text-red-700 rounded-lg text-xs font-medium">
                          {t('employeeStats.absent')}
                        </span>
                      )}
                      {item.status === 'OnLeave' && (
                        <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium">
                          {item.leaveType || t('employeeStats.onLeave')}
                        </span>
                      )}
                      {item.status === 'Holiday' && (
                        <span className="px-2.5 py-1 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium">
                          {t('employeeStats.publicHoliday')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Son Hareketler Tablosu */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Calendar size={18} className="text-slate-400" />
                  {t('employeeStats.recentRecords')}
                </h3>
                <p className="text-sm text-slate-500 mt-0.5">{t('employeeStats.last10Records')}</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{t('employeeStats.date')}</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{t('employeeStats.day')}</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                      <div className="flex items-center gap-1">
                        <LogIn size={12} /> {t('employeeStats.checkIn')}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                      <div className="flex items-center gap-1">
                        <LogOut size={12} /> {t('employeeStats.checkOut')}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{t('employeeStats.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{t('employeeStats.workDuration')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {stats.recentEntries.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                        <Clock size={32} className="mx-auto mb-2 opacity-30" />
                        <p>{t('employeeStats.noRecords')}</p>
                      </td>
                    </tr>
                  ) : (
                    stats.recentEntries.map((entry, index) => (
                      <tr key={index} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-slate-700">
                          {formatDate(entry.date)}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500">
                          {formatDayName(entry.date)}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className="px-2.5 py-1 bg-green-50 text-green-700 rounded-lg font-medium">
                            {entry.checkIn}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {entry.checkOut ? (
                            <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg font-medium">
                              {entry.checkOut}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {entry.lateMinutes > 0 ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 rounded-lg font-medium">
                              <Clock size={12} /> {entry.lateMinutes} {t('employeeStats.minLate')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 rounded-lg font-medium">
                              <CheckCircle size={12} /> {t('employeeStats.onTime')}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-slate-700">
                          {entry.workedMinutes > 0 ? formatHours(entry.workedMinutes) : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {/* Photo Zoom Modal */}
      {showPhotoZoom && photoUrl && (
        <div className="fixed inset-0 bg-black/80 z-[100] backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={() => setShowPhotoZoom(false)}>
          <div className="relative max-w-lg w-full animate-in zoom-in-95 duration-300" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowPhotoZoom(false)} className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center text-slate-500 hover:text-red-500 shadow-lg z-10 transition-colors">
              <X size={18} />
            </button>
            <img src={photoUrl} alt={stats?.fullName || ''} className="w-full rounded-2xl shadow-2xl object-contain max-h-[80vh]" />
            <div className="text-center mt-3">
              <span className="text-white/80 text-sm font-medium">{stats?.fullName}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeStats;
